import { Option, Text } from '@byecode/ui'
import { ApplicationSettingNavbarButton, ButtonPattern, ButtonShowType, NavHandleEvent, PageAbstract } from '@lighthouse/core'
import { clone, find, map } from 'rambda'
import React, { useMemo } from 'react'

import { buttonActions, commonPageOptions, PURE_ALLOW_ICON_TYPES } from '../../constants'
import type { ButtonConfig } from '../../types/button'
import { IconPicker } from '../IconPicker'
import { BasicListItem } from './BasicListItem'
import { btnStyleList, btnTypeList, data, navEventsMap } from './constant'
import Select from './Select'
import * as SC from './styles'

type ConfigItemProps = Omit<ButtonConfig, 'data'> & {
    data: Option[]
    value: string
    onChange: (value: string) => void
}

const ConfigItem: React.FC<ConfigItemProps> = React.memo(({ value, placeHolder, label, type, name, data, onChange }) => {
    return (
        <BasicListItem position="apart" noWrap>
            <SC.Text>{label}</SC.Text>
            <SC.RightFill>
                {type === 'input' ? (
                    <SC.BlockSettingsInput
                        value={value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                        placeholder={placeHolder}
                    />
                ) : (
                    <SC.BlockSettingsSelect
                        styles={{
                            root: {
                                width: '100%'
                            }
                        }}
                        clearable
                        searchable
                        name="pointer"
                        value={value}
                        options={data}
                        onChange={val => {
                            onChange(val ?? '')
                        }}
                        placeholder={data.length === 0 ? `暂无可选${label}` : placeHolder}
                    />
                )}
            </SC.RightFill>
        </BasicListItem>
    )
})

interface ButtonSettingsProps {
    disabled?: boolean
    allPages: PageAbstract[]
    value: ApplicationSettingNavbarButton
    style?: React.CSSProperties
    disableHandleEvent?: boolean
    allViews?: { title: string; id: string; dsId: string }[]
    onChange: (params: ApplicationSettingNavbarButton) => void
}

export const ButtonSetting: React.FC<ButtonSettingsProps> = ({
    disabled,
    value,
    allPages,
    style,
    disableHandleEvent = false,
    onChange
}) => {
    const {
        events: { handleEvent, params },
        icon,
        name,
        showType,
        pattern,
        events
    } = value

    const action = useMemo(() => find(item => item.value === handleEvent, data), [handleEvent])

    const buttonAction = useMemo(() => buttonActions[action?.value ?? NavHandleEvent.OPEN_PAGE], [action?.value])

    const pageSelectList = useMemo(
        () => [
            ...commonPageOptions,
            ...map(({ id, name }) => ({ label: name || '无标题', value: `/page/${id}`, group: 'default' }), allPages)
        ],
        [allPages]
    )

    const currentItem = useMemo(() => find(item => item.value === handleEvent, data), [handleEvent])

    const renderConfig = useMemo(
        () =>
            buttonAction.map(item => {
                let selectData: Option[] = []
                if (item.name === 'openPageId') {
                    selectData = pageSelectList
                }

                return (
                    <ConfigItem
                        key={item.name}
                        {...item}
                        value={params?.[item.index]}
                        data={selectData}
                        onChange={val => {
                            const newParams = clone(params)
                            newParams[item.index] = val
                            onChange({ ...value, events: { ...events, params: newParams } })
                        }}
                    />
                )
            }),
        [buttonAction, events, onChange, pageSelectList, params, value]
    )

    return (
        <SC.Container style={style}>
            <BasicListItem
                position="apart"
                styles={{
                    root: {
                        gap: 0
                    }
                }}
            >
                <SC.Text>文本</SC.Text>
                <SC.RightFill>
                    <SC.BlockSettingsInput
                        width="calc(100% - 40px)"
                        value={name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, name: e.target.value })}
                        placeholder="输入按钮名称"
                    />
                    <IconPicker
                        allowIconTypes={PURE_ALLOW_ICON_TYPES}
                        disabled={disabled}
                        value={{ type: 'icon', value: icon }}
                        onChange={icon => onChange({ ...value, icon: icon?.value ?? '' })}
                    />
                </SC.RightFill>
            </BasicListItem>
            <BasicListItem
                position="apart"
                styles={{
                    root: {
                        gap: 0
                    }
                }}
            >
                <SC.Text>按钮类型</SC.Text>
                <SC.RightFill>
                    <SC.BlockSettingsSelect
                        styles={{
                            root: {
                                width: '100%'
                            }
                        }}
                        name="pointer"
                        value={showType}
                        options={btnTypeList}
                        onChange={val => onChange({ ...value, showType: val as ButtonShowType })}
                    />
                </SC.RightFill>
            </BasicListItem>
            {!disableHandleEvent && (
                <>
                    {pattern !== undefined && (
                        <BasicListItem
                            position="apart"
                            styles={{
                                root: {
                                    gap: 0
                                }
                            }}
                        >
                            <SC.Text>按钮样式</SC.Text>
                            <SC.RightFill>
                                <SC.BlockSettingsSelect
                                    styles={{
                                        root: {
                                            width: '100%'
                                        }
                                    }}
                                    name="pointer"
                                    value={pattern?.toString()}
                                    options={btnStyleList}
                                    onChange={val => onChange({ ...value, pattern: Number(val) })}
                                />
                            </SC.RightFill>
                        </BasicListItem>
                    )}
                    <Select
                        label="就"
                        value={handleEvent}
                        disabled={disabled}
                        onChange={val => {
                            onChange({
                                ...value,
                                events: {
                                    ...(navEventsMap.get(val as NavHandleEvent) ?? {
                                        handleEvent: NavHandleEvent.OPEN_PAGE,
                                        triggerEvent: 'click',
                                        params: ['']
                                    })
                                }
                            })
                        }}
                        data={data}
                        target={
                            <SC.RightFill>
                                <SC.Button bgColor="var(--color-black)">
                                    <SC.Icon
                                        type={currentItem?.icon ?? 'BlockLinkToPage'}
                                        bgColor="var(--color-gray-500)"
                                        color="var(--color-white)"
                                    />
                                    <Text color="var(--color-white)">{currentItem?.label}</Text>
                                    <SC.Icon type="CaretDown" color="var(--color-white)" />
                                </SC.Button>
                            </SC.RightFill>
                        }
                    />
                    {renderConfig}
                </>
            )}
        </SC.Container>
    )
}
